import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makePageT } from '../../shared/utils/pageI18n';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Search, Plus, RefreshCcw, TrendingUp, TrendingDown, Filter, Eye, X, XCircle, LayoutGrid, List, LayoutList, Upload, Download, FileSpreadsheet, FileText, FileOutput, Calendar, ChevronDown, ChevronUp, CheckCircle, DollarSign, Package, Users, ShoppingCart, AlertCircle, Shield, Layers } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import AchatsFormModalTabs from './components/AchatsFormModalTabs';
import { buildAchatsColumnDefs, achatsColHeaderProps, ACHATS_FORM_MODAL_SHELL, ACHATS_FORM_TAB_PANEL, ACHATS_FORM_GRID, useAchatsModalTheme, useAchatsColumnVisibility, achatsTableColSpan, ACHATS_TABLE_ACTIONS_TH_CLASS, useAchatsRowSelection, buildAchatsBulkDeleteFilterAction, AchatsBulkDeleteBar, useAppNotificationToast, AchatsCrudNotificationToast, sortRecordsNewestFirst, notifyCreated, notifyUpdated, notifyDeleted, notifyBulkDeleted, notifyError, useAchatsListViewMode, useAchatsListDisplay } from './achatsListPageUtils';
import { useAchatsConfig } from './useAchatsConfig';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import '../../shared/components/custom/CustomComponents.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

const NS = 'modules.achats.pages.strategieAchatPage';

const StrategieAchatPage = () => {
  const { t, i18n } = useTranslation();
  const p = makePageT(t, 'modules.achats.pages.strategieAchatPage');
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const { cfgTheme, modal: m } = useAchatsModalTheme();
  const achatsConfig = useAchatsConfig();
  const dateLocale = getBrandLocale(i18n.language);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(dateLocale) : '-';
  const [searchTerm, setSearchTerm] = useState('');
  const [strategies, setStrategies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [selectedStrategies, setSelectedStrategies] = useState([]);
  const [viewMode, setViewMode] = useAchatsListViewMode(achatsConfig);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingStrategy, setViewingStrategy] = useState(null);
  
  // Collapsible sections state
  const [formTab, setFormTab] = useState('general');
  const formTabs = useMemo(() => ([
    { id: 'general', label: p('section_basic_info'), icon: FileText, iconColor: 'text-blue-400' },
    { id: 'details', label: p('section_actions'), icon: Target, iconColor: 'text-green-400' },
  ]), [p]);
  const [formData, setFormData] = useState({ 
    strategy_name: '', 
    strategy_type: 'cost_reduction',
    status: 'draft',
    start_date: new Date().toISOString().split('T')[0], 
    end_date: '', 
    budget_allocated: '', 
    current_spend: '',
    target_savings: '',
    supplier_count: '',
    category: '',
    priority: 'medium',
    responsible_person: '',
    description: '',
    kpis: '',
    action_plan: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_strategies');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { loadStrategies(); }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadStrategies = async () => {
    try {
      const response = await localApiAdapter.get('/achats/strategie');
      setStrategies(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading strategies:', error);
      setStrategies([]);
    }
  };

  const stats = {
    total: strategies.length,
    active: strategies.filter(s => s.status === 'active').length,
    draft: strategies.filter(s => s.status === 'draft').length,
    completed: strategies.filter(s => s.status === 'completed').length,
    totalBudget: strategies.reduce((sum, s) => sum + (parseFloat(s.budget_allocated) || 0), 0),
    totalSavings: strategies.reduce((sum, s) => sum + (parseFloat(s.target_savings) || 0), 0)
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_strategies', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'par_type', name: t('common.by_type'), icon: Shield },
    { id: 'par_statut', name: t('common.by_status'), icon: Target }
  ]), [t]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: p('quick_all'), icon: Layers },
    { id: 'active', name: p('quick_active'), icon: TrendingUp },
    { id: 'draft', name: p('dist_draft'), icon: FileText },
    { id: 'completed', name: p('quick_completed'), icon: CheckCircle }
  ]), [t, p]);

  const strategyDistribution = useMemo(() => ({
    all_strategies: {
      title: p('dist_overview'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'draft', name: p('dist_draft'), count: stats.draft, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
        { filterKey: 'completed', name: p('dist_completed'), count: stats.completed, color: 'bg-gradient-to-r from-blue-500 to-blue-600' }
      ]
    },
    par_type: {
      title: p('dist_by_type'),
      data: [
        { filterKey: 'cost_reduction', name: p('dist_cost_reduction'), count: strategies.filter(s => s.strategy_type === 'cost_reduction').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'supplier_consolidation', name: p('dist_consolidation'), count: strategies.filter(s => s.strategy_type === 'supplier_consolidation').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'quality_improvement', name: p('dist_quality'), count: strategies.filter(s => s.strategy_type === 'quality_improvement').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'other', name: p('dist_other'), count: strategies.filter(s => !['cost_reduction', 'supplier_consolidation', 'quality_improvement'].includes(s.strategy_type)).length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' }
      ]
    },
    par_statut: {
      title: p('dist_by_status'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'draft', name: p('dist_preparation'), count: stats.draft, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'completed', name: p('dist_completed'), count: stats.completed, color: 'bg-gradient-to-r from-blue-500 to-blue-600' }
      ]
    }
  }), [strategies, stats, p]);

  const distributionInfo = strategyDistribution[mainViewMode] || strategyDistribution.all_strategies;

  const baseFilteredStrategies = strategies.filter(s => {
    // Date range filter
    if (dateRange.start || dateRange.end) {
      const strategyDate = new Date(s.start_date);
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (strategyDate < startOfDay) return false;
      }
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (strategyDate > endOfDay) return false;
      }
    }
    
    // Search filter
    if (searchTerm && !s.strategy_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    // Quick filter (only for Vue Générale)
    if (mainViewMode === 'all_strategies') {
      if (quickFilter === 'active' && s.status !== 'active') return false;
      if (quickFilter === 'draft' && s.status !== 'draft') return false;
      if (quickFilter === 'completed' && s.status !== 'completed') return false;
    }
    
    // Distribution card filter (clickable stats)
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_strategies':
          if (activeDistributionFilter === 'active' && s.status !== 'active') return false;
          if (activeDistributionFilter === 'draft' && s.status !== 'draft') return false;
          if (activeDistributionFilter === 'completed' && s.status !== 'completed') return false;
          break;
        case 'par_type':
          if (activeDistributionFilter === 'cost_reduction' && s.strategy_type !== 'cost_reduction') return false;
          if (activeDistributionFilter === 'supplier_consolidation' && s.strategy_type !== 'supplier_consolidation') return false;
          if (activeDistributionFilter === 'quality_improvement' && s.strategy_type !== 'quality_improvement') return false;
          if (activeDistributionFilter === 'other' && ['cost_reduction', 'supplier_consolidation', 'quality_improvement'].includes(s.strategy_type)) return false;
          break;
        case 'par_statut':
          if (activeDistributionFilter === 'active' && s.status !== 'active') return false;
          if (activeDistributionFilter === 'draft' && s.status !== 'draft') return false;
          if (activeDistributionFilter === 'completed' && s.status !== 'completed') return false;
          break;
      }
    }
    
    return true;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { 
        strategy_name: formData.strategy_name,
        strategy_type: formData.strategy_type,
        status: formData.status,
        start_date: formData.start_date,
        end_date: formData.end_date,
        budget_allocated: parseFloat(formData.budget_allocated) || 0,
        current_spend: parseFloat(formData.current_spend) || 0,
        target_savings: parseFloat(formData.target_savings) || 0,
        supplier_count: parseInt(formData.supplier_count) || 0,
        category: formData.category,
        priority: formData.priority,
        responsible_person: formData.responsible_person,
        description: formData.description,
        kpis: formData.kpis,
        action_plan: formData.action_plan
      };
      if (editingStrategy) {
        await localApiAdapter.put(`/achats/strategies/${editingStrategy.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/achats/strategie', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('empty_dash') }));
    } finally {
      resetForm();
      loadStrategies();
    }
  };

  const handleEdit = (strategy) => {
    setEditingStrategy(strategy);
    setFormData({ 
      strategy_name: strategy.strategy_name || '',
      strategy_type: strategy.strategy_type || 'cost_reduction',
      status: strategy.status || 'draft',
      start_date: strategy.start_date || '',
      end_date: strategy.end_date || '',
      budget_allocated: strategy.budget_allocated?.toString() || '',
      current_spend: strategy.current_spend?.toString() || '',
      target_savings: strategy.target_savings?.toString() || '',
      supplier_count: strategy.supplier_count?.toString() || '',
      category: strategy.category || '',
      priority: strategy.priority || 'medium',
      responsible_person: strategy.responsible_person || '',
      description: strategy.description || '',
      kpis: strategy.kpis || '',
      action_plan: strategy.action_plan || ''
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/achats/strategies/${id}`);
        notifyDeleted(showNotify, entityLabel);
        loadStrategies();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: p('empty_dash') }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedStrategies.length }))) {
      try {
        const deleteCount = selectedStrategies.length;
        await Promise.all(selectedStrategies.map(id => localApiAdapter.delete(`/achats/strategies/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabel);
        setSelectedStrategies([]);
        loadStrategies();
      } catch (error) {
        console.error('Error deleting strategies:', error);
        notifyError(showNotify, p('alert_save_error', { message: p('empty_dash') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      strategy_name: '', 
      strategy_type: 'cost_reduction',
      status: 'draft',
      start_date: '', 
      end_date: '', 
      budget_allocated: '', 
      current_spend: '',
      target_savings: '',
      supplier_count: '',
      category: '',
      priority: 'medium',
      responsible_person: '',
      description: '',
      kpis: '',
      action_plan: ''
    });
    setEditingStrategy(null);
    setFormTab('general');
    setShowModal(false);
  };

  const handleExport = () => {
    const headers = [
      { key: 'strategy_name', label: p('export_col_strategy_name') },
      { key: 'strategy_type', label: p('export_col_type') },
      { key: 'status', label: p('export_col_status') },
      { key: 'start_date', label: p('export_col_start') },
      { key: 'end_date', label: p('export_col_end') },
      { key: 'budget_allocated', label: p('export_col_budget') },
      { key: 'target_savings', label: p('export_col_savings') },
      { key: 'priority', label: p('export_col_priority') }
    ];
    const fileName = `strategies_achat_${new Date().toISOString().split('T')[0]}`;
    exportToExcel(filteredStrategies, headers, `${fileName}.xlsx`);
  };

  const handleImport = () => {
    const headers = [
      { key: 'strategy_name', label: p('export_col_strategy_name') },
      { key: 'strategy_type', label: p('export_col_type') },
      { key: 'status', label: p('export_col_status') },
      { key: 'start_date', label: p('export_col_start') },
      { key: 'budget_allocated', label: p('export_col_budget') }
    ];
    const schema = {
      requiredFields: ['strategy_name', 'strategy_type'],
      validators: {
        budget_allocated: (value) => !value || !isNaN(parseFloat(value))
      }
    };
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/achats/strategie', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          await loadStrategies();
          alert(p('alert_import_success', { total: result.totalCount, imported: successCount }));
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };


  const handleSelectAll = () => {
    if (rowSelection.allRowsSelected) {
      setSelectedStrategies([]);
    } else {
      setSelectedStrategies(filteredStrategies.map((r) => r.id));
    }
  };
  
  const handleSelectStrategy = (id) => { 
    setSelectedStrategies(selectedStrategies.includes(id) ? selectedStrategies.filter(sId => sId !== id) : [...selectedStrategies, id]); 
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/achats/strategies/${recordId}`, { status: newStatus });
      await loadStrategies();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find((s) => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>{Icon ? <Icon size={14} /> : null}{label}</span>;
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { label: p('priority_low'), style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
      medium: { label: p('priority_medium'), style: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
      high: { label: p('priority_high'), style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
      critical: { label: p('priority_critical'), style: 'bg-red-500/20 text-red-300 border-red-500/30' }
    };
    const config = priorityConfig[priority] || priorityConfig.medium;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${config.style}`}>{config.label}</span>;
  };

  const strategyTypeOptions = useMemo(() => ([
    { value: 'cost_reduction', label: p('strategy_cost_reduction') },
    { value: 'supplier_consolidation', label: p('strategy_consolidation') },
    { value: 'quality_improvement', label: p('strategy_quality') },
    { value: 'risk_mitigation', label: p('strategy_risk') },
    { value: 'sustainability', label: p('strategy_sustainability') },
    { value: 'innovation', label: p('strategy_innovation') }
  ]), [p]);

  const statusOptions = useMemo(() => ([
    { value: 'draft', label: p('status_draft'), icon: FileText, style: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
    { value: 'active', label: p('status_active_f'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'completed', label: p('status_completed_f'), icon: Shield, style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
    { value: 'cancelled', label: p('status_cancelled_f'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' }
  ]), [p]);

  const priorityOptions = useMemo(() => ([
    { value: 'low', label: p('priority_low') },
    { value: 'medium', label: p('priority_medium') },
    { value: 'high', label: p('priority_high') },
    { value: 'critical', label: p('priority_critical') }
  ]), [p]);

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('strategy_name'),
      exactColumnRule('strategy_type', (row) => row.strategy_type || ''),
      textColumnRule('start_date', (row) => (row.start_date ? String(row.start_date).split('T')[0] : '')),
      textColumnRule('budget_allocated', (row) => String(row.budget_allocated ?? '')),
      textColumnRule('target_savings', (row) => String(row.target_savings ?? '')),
      exactColumnRule('priority', (row) => row.priority || ''),
      exactColumnRule('status', (row) => row.status || ''),
    ],
    [],
  );

  const { filtered: filteredStrategies, displayed: displayedStrategies } = useAchatsListDisplay(
    baseFilteredStrategies,
    colFilters,
    columnFilterRules,
    achatsConfig.itemsPerPage,
  );

  const rowSelection = useAchatsRowSelection(selectedStrategies, filteredStrategies);

  const colHeader = (columnKey, label, opts = {}) =>
    achatsColHeaderProps({ columnKey, label, colFilters, setColFilter, editingKey, setEditingKey, ...opts });

  const categoryOptions = useMemo(() => ([
    { value: 'matières_premières', label: p('category_raw_materials') },
    { value: 'services', label: p('category_services') },
    { value: 'equipements', label: p('category_equipment') },
    { value: 'fournitures', label: p('category_supplies') },
    { value: 'transport', label: p('category_transport') }
  ]), [p]);

  const kpiStats = useMemo(() => ([
    { title: p('stat_total'), value: stats.total, icon: Target, color: 'from-blue-500 to-blue-600' },
    { title: p('stat_active'), value: stats.active, icon: TrendingUp, color: 'from-green-500 to-green-600' },
    { title: p('dist_draft'), value: stats.draft, icon: FileText, color: 'from-gray-500 to-gray-600' },
    { title: p('stat_completed'), value: stats.completed, icon: CheckCircle, color: 'from-purple-500 to-purple-600' },
    { title: p('stat_total_budget'), value: formatBrandMoney(stats.totalBudget), icon: DollarSign, color: 'from-amber-500 to-amber-600' },
    { title: p('stat_target_savings'), value: formatBrandMoney(stats.totalSavings), icon: TrendingDown, color: 'from-red-500 to-red-600' }
  ]), [stats, p]);

  const strategieColumnKeys = useMemo(
    () => ['strategy_name', 'strategy_type', 'start_date', 'budget_allocated', 'target_savings', 'priority', 'status', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useAchatsColumnVisibility(strategieColumnKeys);
  const qawaemColumns = useMemo(
    () => buildAchatsColumnDefs([
      { key: 'strategy_name', labelKey: 'col_strategy_name' },
      { key: 'strategy_type', labelKey: 'label_type' },
      { key: 'start_date', labelKey: 'label_period' },
      { key: 'budget_allocated', labelKey: 'col_budget_allocated' },
      { key: 'target_savings', labelKey: 'col_savings' },
      { key: 'priority', labelKey: 'label_priority' },
      { key: 'status', labelKey: 'label_status' },
      { key: 'actions', label: t('common.actions'), required: true },
    ], p, columnVisibility),
    [p, t, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = achatsTableColSpan(qawaemColumns);

  return (
    <div className={`space-y-6 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Target className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadStrategies} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} 
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{t('common.advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button 
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" 
            onClick={() => { setEditingStrategy(null); setFormTab('general');
    setShowModal(true); }}
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_item')}</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiStats.slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options */}
      <motion.div 
        initial={false} 
        animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} 
        transition={{ duration: 0.3, ease: "easeInOut" }} 
        style={{ overflow: "hidden" }}
      >
        <div className="space-y-6 pt-6">
          {/* Main Tabs */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button 
                  key={tab.id} 
                  onClick={() => { 
                    setMainViewMode(tab.id); 
                    setQuickFilter('all'); 
                    setActiveDistributionFilter(null); 
                  }} 
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    mainViewMode === tab.id 
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' 
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Quick Filter Buttons (only for Vue Générale) */}
          {mainViewMode === 'all_strategies' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button 
                    key={filter.id} 
                    onClick={() => setQuickFilter(filter.id)} 
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                      quickFilter === filter.id 
                        ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' 
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <filter.icon className="h-5 w-5" />
                    <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Distribution Cards */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }} 
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button 
                  onClick={() => setActiveDistributionFilter(null)}
                  className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                >
                  <XCircle size={14} />
                  {p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3', 'lg:grid-cols-4')}>{distributionInfo.data.map((item) => (
                <button
                  key={item.filterKey}
                  onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${
                    activeDistributionFilter === item.filterKey 
                      ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' 
                      : 'border-white/10 hover:border-white/20 hover:bg-white/10'
                  }`}
                  title={p('filter_click_title', { name: item.name })}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>
                      {item.name}
                    </h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>
                      {item.count}
                    </div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div 
                      className={`h-3 rounded-full ${item.color} transition-all`} 
                      style={{ 
                        width: `${(() => {
                          const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                          return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                        })()}%` 
                      }}
                    ></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>
                    {(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      const pct = total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                      return p('pct_of_strategies', { pct });
                    })()}
                  </p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Table Container */}
      <div className="glass-panel glass-table-shell overflow-visible">
        <AchatsBulkDeleteBar
          selectedCount={selectedStrategies.length}
          onDelete={handleBulkDelete}
          deleteLabel={`${t('common.delete')} (${selectedStrategies.length})`}
        />

        {/* Table View */}
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={rowSelection.allRowsSelected}
                        indeterminate={rowSelection.someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  
                  {isColumnVisible('strategy_name') ? <TableColumnHeaderFilter {...colHeader('strategy_name', p('col_strategy_name'))} /> : null}
                  {isColumnVisible('strategy_type') ? <TableColumnHeaderFilter {...colHeader('strategy_type', p('label_type'), { kind: 'select', options: strategyTypeOptions })} /> : null}
                  {isColumnVisible('start_date') ? <TableColumnHeaderFilter {...colHeader('start_date', p('label_period'))} /> : null}
                  {isColumnVisible('budget_allocated') ? <TableColumnHeaderFilter {...colHeader('budget_allocated', p('col_budget_allocated'), { align: 'center' })} /> : null}
                  {isColumnVisible('target_savings') ? <TableColumnHeaderFilter {...colHeader('target_savings', p('col_savings'), { align: 'center' })} /> : null}
                  {isColumnVisible('priority') ? <TableColumnHeaderFilter {...colHeader('priority', p('label_priority'), { kind: 'select', options: priorityOptions, align: 'center' })} /> : null}
                  {isColumnVisible('status') ? <TableColumnHeaderFilter {...colHeader('status', p('label_status'), { kind: 'select', options: statusOptions, align: 'center' })} /> : null}
                  <QawaemTableHeaderActionsCell
                    thClassName={ACHATS_TABLE_ACTIONS_TH_CLASS}
                    label={t('common.actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedStrategies.length > 0 ? (
                  displayedStrategies.map((strategy, index) => (
                    <tr 
                      key={strategy.id} 
                      className={`hover:bg-white/5 transition-colors ${selectedStrategies.includes(strategy.id) ? 'bg-white/10' : ''}`}
                    >
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedStrategies.includes(strategy.id)}
                              onChange={() => handleSelectStrategy(strategy.id)}
                            />
                          </div>
                        </td>
                      {isColumnVisible('strategy_name') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{strategy.strategy_name}</span>
                          <span className="text-white/50 text-xs mt-0.5">{strategy.category || p('uncategorized')}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColumnVisible('strategy_type') ? (
                      <td className="px-6 py-4">
                        <span className="text-white/80">{strategyTypeOptions.find(t => t.value === strategy.strategy_type)?.label || strategy.strategy_type}</span>
                      </td>
                      ) : null}
                      {isColumnVisible('start_date') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{fmtDate(strategy.start_date)}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {strategy.end_date ? `${p('end_prefix')} ${fmtDate(strategy.end_date)}` : p('period_end_none')}
                          </span>
                        </div>
                      </td>
                      ) : null}
                      {isColumnVisible('budget_allocated') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{formatBrandMoney((strategy.budget_allocated || 0))}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('export_col_spent')}: {formatBrandMoney((strategy.current_spend || 0))}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColumnVisible('target_savings') ? (
                      <td className="px-6 py-4">
                        <span className="text-green-300 font-semibold">{formatBrandMoney((strategy.target_savings || 0))}</span>
                      </td>
                      ) : null}
                      {isColumnVisible('priority') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">{getPriorityBadge(strategy.priority)}</div>
                      </td>
                      ) : null}
                      {isColumnVisible('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge status={strategy.status} recordId={strategy.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingStrategy(strategy); setShowViewModal(true); }}
                          onEdit={() => handleEdit(strategy)}
                          onDelete={() => handleDelete(strategy.id)}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Target size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Card View */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedStrategies.length > 0 ? (
              displayedStrategies.map((strategy, index) => (
                <motion.div
                  key={strategy.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Target size={20} className="text-blue-400" />
                          {strategy.strategy_name}
                        </h3>
                        <div className="flex gap-2">
                          <QawaemClickableStatusBadge status={strategy.status} recordId={strategy.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                          {getPriorityBadge(strategy.priority)}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_type')}:</span> {strategyTypeOptions.find(t => t.value === strategy.strategy_type)?.label}</div>
                            <div><span className="font-medium">{p('label_category')}:</span> {strategy.category || p('uncategorized')}</div>
                            <div><span className="font-medium">{p('label_responsible')}:</span> {strategy.responsible_person || p('na')}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_period')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_start')}:</span> {fmtDate(strategy.start_date)}</div>
                            <div><span className="font-medium">{p('label_end')}:</span> {fmtDate(strategy.end_date)}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_budget')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_allocated')}:</span> {formatBrandMoney((strategy.budget_allocated || 0))}</div>
                            <div><span className="font-medium">{p('export_col_spent')}:</span> {formatBrandMoney((strategy.current_spend || 0))}</div>
                            <div><span className="font-medium">{p('label_savings')}:</span> <span className="text-green-300">{formatBrandMoney((strategy.target_savings || 0))}</span></div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('label_suppliers')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_count')}:</span> {strategy.supplier_count || 0}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingStrategy(strategy); setShowViewModal(true); }}
                      onEdit={() => handleEdit(strategy)}
                      onDelete={() => handleDelete(strategy.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Target size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* View Modal */}
      {showViewModal && viewingStrategy ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_title')}
        icon={Eye}
        testId="achats-view-modal"
      >
        <div className="space-y-4">
                <div className={m.viewSection}>
                  <h3 className={m.viewSectionTitle}>{p('section_general')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className={m.viewLabel}>{p('label_name')}</p>
                      <p className={m.viewValue}>{viewingStrategy.strategy_name}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_type')}</p>
                      <p className={m.viewValue}>{strategyTypeOptions.find(t => t.value === viewingStrategy.strategy_type)?.label}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_status')}</p>
                      <div className="flex">{getStatusBadge(viewingStrategy.status)}</div>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_priority')}</p>
                      <div className="flex">{getPriorityBadge(viewingStrategy.priority)}</div>
                    </div>
                  </div>
                </div>

                {viewingStrategy.description && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('label_description')}</h3>
                    <p className="text-white/80">{viewingStrategy.description}</p>
                  </div>
                )}

                {viewingStrategy.kpis && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('label_kpis')}</h3>
                    <p className="text-white/80 whitespace-pre-wrap">{viewingStrategy.kpis}</p>
                  </div>
                )}

                {viewingStrategy.action_plan && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('label_action_plan')}</h3>
                    <p className="text-white/80 whitespace-pre-wrap">{viewingStrategy.action_plan}</p>
                  </div>
                )}
        </div>
      </QawaemErpViewModal>
      ) : null}

      {/* Edit/Create Modal - WITH CUSTOM COMPONENTS */}
      <QawaemErpCrudModal
        open={showModal}
        onClose={() => resetForm()}
        title={editingStrategy ? p('modal_edit') : p('modal_create')}
        icon={Target}
        formId="strategy-form"
        saveLabel={t('common.save')}
        onSubmit
        testId="achats-crud-modal"
      >
        <div className={ACHATS_FORM_MODAL_SHELL}>
          <AchatsFormModalTabs
            tabs={formTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            isLight={m.isLight}
            ariaLabel={editingStrategy ? p('modal_edit') : p('modal_create')}
          />
          <div className={ACHATS_FORM_TAB_PANEL}>
            <form id="strategy-form" onSubmit={handleSubmit}>
              {formTab === 'general' && (
                <div className={ACHATS_FORM_GRID}>
                  <div>
                    <label className={cfgTheme.label}><RequiredLabelText text={p('label_strategy_name')} required /></label>
                    <input
                      type="text"
                      required
                      value={formData.strategy_name}
                      onChange={(e) => setFormData({ ...formData, strategy_name: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_strategy_name')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_strategy_type')}</label>
                    <CustomSelect
                      value={formData.strategy_type}
                      onChange={(val) => setFormData({ ...formData, strategy_type: val || 'cost_reduction' })}
                      options={strategyTypeOptions}
                      placeholder={p('label_strategy_type')}
                      searchable
                      autoSelect={false}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_status')}</label>
                    <CustomSelect
                      value={formData.status}
                      onChange={(val) => setFormData({ ...formData, status: val || 'draft' })}
                      options={statusOptions}
                      placeholder={p('label_status')}
                      searchable
                      autoSelect={false}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_category')}</label>
                    <CustomSelect
                      value={formData.category}
                      onChange={(val) => setFormData({ ...formData, category: val || '' })}
                      options={categoryOptions}
                      placeholder={p('label_category')}
                      searchable
                      creatable
                      autoSelect={false}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_priority')}</label>
                    <CustomSelect
                      value={formData.priority}
                      onChange={(val) => setFormData({ ...formData, priority: val || 'medium' })}
                      options={priorityOptions}
                      placeholder={p('label_priority')}
                      searchable
                      autoSelect={false}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_responsible')}</label>
                    <input
                      type="text"
                      value={formData.responsible_person}
                      onChange={(e) => setFormData({ ...formData, responsible_person: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_responsible')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_start_date')}</label>
                    <CustomDatePicker
                      value={formData.start_date}
                      onChange={(date) => setFormData({ ...formData, start_date: date })}
                      placeholder={p('placeholder_start_date')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_end_date')}</label>
                    <CustomDatePicker
                      value={formData.end_date}
                      onChange={(date) => setFormData({ ...formData, end_date: date })}
                      placeholder={p('placeholder_end_date')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_budget_allocated')}</label>
                    <CustomNumberInput
                      value={formData.budget_allocated}
                      onChange={(value) => setFormData({ ...formData, budget_allocated: value })}
                      placeholder="0.00"
                      step={1000}
                      min={0}
                      decimals={2}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_current_spend')}</label>
                    <CustomNumberInput
                      value={formData.current_spend}
                      onChange={(value) => setFormData({ ...formData, current_spend: value })}
                      placeholder="0.00"
                      step={1000}
                      min={0}
                      decimals={2}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_target_savings')}</label>
                    <CustomNumberInput
                      value={formData.target_savings}
                      onChange={(value) => setFormData({ ...formData, target_savings: value })}
                      placeholder="0.00"
                      step={1000}
                      min={0}
                      decimals={2}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_supplier_count')}</label>
                    <CustomNumberInput
                      value={formData.supplier_count}
                      onChange={(value) => setFormData({ ...formData, supplier_count: value })}
                      placeholder="0"
                      step={1}
                      min={0}
                      decimals={0}
                    />
                  </div>
                </div>
              )}

              {formTab === 'details' && (
                <div className={ACHATS_FORM_GRID}>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('label_description')}</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[100px]"
                      placeholder={p('placeholder_strategy_detail')}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('label_kpis')}</label>
                    <textarea
                      value={formData.kpis}
                      onChange={(e) => setFormData({ ...formData, kpis: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[100px]"
                      placeholder={p('placeholder_kpis')}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('label_action_plan')}</label>
                    <textarea
                      value={formData.action_plan}
                      onChange={(e) => setFormData({ ...formData, action_plan: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[150px]"
                      placeholder={p('placeholder_action_plan')}
                    />
                  </div>
                </div>
              )}

              </form>
          </div>
        </div>
      </QawaemErpCrudModal>
      <AchatsCrudNotificationToast notifyToast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default StrategieAchatPage;

